import { RaRecord, useNotify, useTranslate } from 'ra-core'
import {
    Datagrid,
    FunctionField,
    List,
    ListProps,
    Pagination,
    TextField,
    useRedirect
} from 'react-admin'

import FileCopyIcon from '@mui/icons-material/FileCopy'
import { Tooltip } from '@mui/material'
import BulkActionButtons from 'components/common/BulkActionButtons'
import DatagridPublishedColumnContent from 'components/common/DatagridPublishedColumnContent'
import { MouseEvent } from 'react'
import { useDuplicateRuleMutation } from '../../apollo/personalization/mutations/DuplicateRule.generated'
import { usePersonalizationRuleBuilderConfigQuery } from '../../apollo/personalization/queries/PersonalizationRuleBuilderConfig.generated'
import { DEFAULT_PER_PAGE, ROWS_PER_PAGE } from '../../globalVariables'
import ExpandableDatagridBody from './components/ExpandableDatagridBody'
import ExpandableDatagridHeader from './components/ExpandableDatagridHeader'
import PersonalizationRuleListFilter from './PersonalizationRuleListFilters'
import { extractActionTypes, extractRuleInputs, formatDisplayList } from './utils/parseRuleData'

const PersonalizationRuleList = (props: ListProps) => {
    const translate = useTranslate()
    const [duplicateRuleMutation] = useDuplicateRuleMutation()
    const redirect = useRedirect()
    const notify = useNotify()
    const { data: builderConfig } = usePersonalizationRuleBuilderConfigQuery()

    const handleDuplicateClick = (e: MouseEvent<SVGSVGElement>, id: string) => {
        e?.stopPropagation() // Stops rowClick from firing
        duplicateRuleMutation({
            variables: { id: id }
        })
            .then((result) => {
                redirect('edit', '/Rule', result.data?.duplicateRule?.id)
            })
            .catch((error) => notify(error.message, { type: 'error' }))
    }

    return (
        <List
            {...props}
            sx={{
                '& .RaDatagrid-headerCell': {
                    fontWeight: 700
                },

                '& .RaList-actions': {
                    position: 'relative'
                },
                form: {
                    minHeight: 'unset',
                    alignItems: 'center',
                    '.MuiFormControl-root': {
                        marginTop: '0 !important'
                    },
                    '.filter-field': {
                        alignItems: 'center',
                        '.RaFilterFormInput-hideButton': {
                            marginBottom: 0
                        }
                    }
                },
                '& .RaBulkActionsToolbar-root': {
                    transform: 'unset !important'
                },
                '& .RaBulkActionsToolbar-toolbar': {
                    transform: 'unset !important'
                }
            }}
            sort={{ field: 'label', order: 'DESC' }}
            perPage={DEFAULT_PER_PAGE}
            filters={PersonalizationRuleListFilter}
            pagination={<Pagination rowsPerPageOptions={ROWS_PER_PAGE} />}
            empty={false}
        >
            <Datagrid
                body={<ExpandableDatagridBody builderConfig={builderConfig} />}
                header={<ExpandableDatagridHeader />}
                rowClick="edit"
                bulkActionButtons={<BulkActionButtons />}
            >
                <FunctionField label="" render={() => null} sortable={false} source="" />
                <TextField source="label" label={translate('manager.resources.rule.label')} />
                <FunctionField
                    source="rules"
                    label={translate('manager.resources.rule.rule_inputs')}
                    render={(record: RaRecord) => {
                        const inputs = extractRuleInputs(record.rules || '{}', builderConfig)
                        return (
                            <span
                                style={{
                                    maxWidth: '200px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'inline-block'
                                }}
                                title={formatDisplayList(inputs)}
                            >
                                {formatDisplayList(inputs)}
                            </span>
                        )
                    }}
                    sortable={false}
                />
                <FunctionField
                    source="actions"
                    label={translate('manager.resources.rule.actions_applied')}
                    render={(record: RaRecord) => {
                        const actions = extractActionTypes(record.actions || '{}')
                        return (
                            <span
                                style={{
                                    maxWidth: '200px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'inline-block'
                                }}
                                title={formatDisplayList(actions)}
                            >
                                {formatDisplayList(actions)}
                            </span>
                        )
                    }}
                    sortable={false}
                />
                <FunctionField
                    source="active"
                    label={translate('manager.resources.general.status')}
                    render={(record: RaRecord) => (
                        <DatagridPublishedColumnContent record={record} />
                    )}
                />
                <FunctionField
                    label={translate('manager.resources.general.actions')}
                    render={(record: RaRecord) =>
                        record?.id && (
                            <Tooltip title={translate('manager.resources.general.duplicate')}>
                                <FileCopyIcon
                                    sx={{
                                        '&:hover': {
                                            transform: `scale(1.1)`
                                        }
                                    }}
                                    onClick={(e: MouseEvent<SVGSVGElement>) =>
                                        handleDuplicateClick(e, record.id.toString())
                                    }
                                />
                            </Tooltip>
                        )
                    }
                />
            </Datagrid>
        </List>
    )
}
export default PersonalizationRuleList
